/**
 * InfoPopup - Displays tooltip-style information popups
 * Used for showing additional context and help information for customization steps
 */
export default class InfoPopup {
    /**
     * Create a new info popup
     * 
     * @param {HTMLElement} targetElement - Element the popup is attached to
     * @param {object} options - Configuration options
     */
    constructor(targetElement, options = {}) {
        this.targetElement = targetElement;
        this.options = {
            title: '',
            description: '',
            imageSrc: null,
            position: 'top', // 'top', 'right', 'bottom', 'left'
            offsetX: 0,
            offsetY: 0,
            closeOnClickOutside: true,
            showOnHover: true,
            className: '',
            ...options
        };
        
        this.element = null;
        this.isVisible = false;
        this.showTimeout = null;
        this.hideTimeout = null;
        
        this.initialize();
    }
    
    /**
     * Initialize the popup
     */
    initialize() {
        // Create the popup element
        this.element = document.createElement('div');
        this.element.className = `stc-info-popup ${this.options.className}`;
        this.element.style.display = 'none';
        
        // Set content
        this.updateContent();
        
        // Add to DOM
        document.body.appendChild(this.element);
        
        // Bind events
        this.bindEvents();
    }
    
    /**
     * Update popup content
     * 
     * @param {object} options - New content options
     */
    updateContent(options = {}) {
        // Update options if provided
        if (Object.keys(options).length > 0) {
            this.options = { ...this.options, ...options };
        }
        
        // Generate HTML based on options
        let html = '<div class="stc-info-popup-content">';
        
        // Add image if provided
        if (this.options.imageSrc) {
            html += `
                <div class="stc-info-popup-image">
                    <img src="/wp-content/plugins/octo-stc/public/assets${this.options.imageSrc}" alt="${this.options.title || 'Info'}">
                </div>
            `;
        }
        
        // Add text content
        html += '<div class="stc-info-popup-text">';
        if (this.options.title) {
            html += `<h4>${this.options.title}</h4>`;
        }
        if (this.options.description) {
            html += `<p>${this.options.description}</p>`;
        }
        html += '</div></div>';
        
        // Update element content
        this.element.innerHTML = html;
    }
    
    /**
     * Bind event listeners
     */
    bindEvents() {
        if (this.options.showOnHover) {
            // Mouse enter/leave events for the target element
            this.targetElement.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
            this.targetElement.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
            
            // Mouse enter/leave events for the popup itself
            this.element.addEventListener('mouseenter', this.handlePopupMouseEnter.bind(this));
            this.element.addEventListener('mouseleave', this.handlePopupMouseLeave.bind(this));
        }
        
        // Click events
        this.targetElement.addEventListener('click', this.handleClick.bind(this));
        
        if (this.options.closeOnClickOutside) {
            document.addEventListener('click', this.handleDocumentClick.bind(this));
        }
        
        // Resize and scroll events for repositioning
        window.addEventListener('resize', this.handleResize.bind(this));
        window.addEventListener('scroll', this.handleScroll.bind(this), true);
    }
    
    /**
     * Handle mouse enter on target element
     */
    handleMouseEnter() {
        clearTimeout(this.hideTimeout);
        this.showTimeout = setTimeout(() => {
            this.show();
        }, 200); // Small delay to prevent flickering
    }
    
    /**
     * Handle mouse leave on target element
     */
    handleMouseLeave() {
        clearTimeout(this.showTimeout);
        this.hideTimeout = setTimeout(() => {
            // Only hide if mouse isn't over popup
            if (!this.isMouseOverPopup) {
                this.hide();
            }
        }, 200);
    }
    
    /**
     * Handle mouse enter on popup
     */
    handlePopupMouseEnter() {
        this.isMouseOverPopup = true;
        clearTimeout(this.hideTimeout);
    }
    
    /**
     * Handle mouse leave on popup
     */
    handlePopupMouseLeave() {
        this.isMouseOverPopup = false;
        this.hideTimeout = setTimeout(() => {
            this.hide();
        }, 200);
    }
    
    /**
     * Handle click on target element
     * @param {Event} e - Click event
     */
    handleClick(e) {
        e.stopPropagation();
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }
    
    /**
     * Handle click on document
     * @param {Event} e - Click event
     */
    handleDocumentClick(e) {
        if (this.isVisible && 
            !this.element.contains(e.target) && 
            !this.targetElement.contains(e.target)) {
            this.hide();
        }
    }
    
    /**
     * Handle window resize
     */
    handleResize() {
        if (this.isVisible) {
            this.position();
        }
    }
    
    /**
     * Handle scroll
     */
    handleScroll() {
        if (this.isVisible) {
            this.position();
        }
    }
    
    /**
     * Show the popup
     */
    show() {
        if (this.isVisible) return;
        
        this.element.style.display = 'block';
        this.position();
        this.isVisible = true;
        
        // Add active class to target
        this.targetElement.classList.add('stc-info-icon-active');
    }
    
    /**
     * Hide the popup
     */
    hide() {
        if (!this.isVisible) return;
        
        this.element.style.display = 'none';
        this.isVisible = false;
        
        // Remove active class from target
        this.targetElement.classList.remove('stc-info-icon-active');
    }
    
    /**
     * Position the popup relative to the target element
     */
    position() {
        const targetRect = this.targetElement.getBoundingClientRect();
        const popupRect = this.element.getBoundingClientRect();
        
        // Get window dimensions
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Calculate initial position
        let left, top;
        
        switch (this.options.position) {
            case 'right':
                left = targetRect.right + 10 + this.options.offsetX;
                top = targetRect.top + (targetRect.height / 2) - (popupRect.height / 2) + this.options.offsetY;
                break;
            case 'left':
                left = targetRect.left - popupRect.width - 10 + this.options.offsetX;
                top = targetRect.top + (targetRect.height / 2) - (popupRect.height / 2) + this.options.offsetY;
                break;
            case 'bottom':
                left = targetRect.left + (targetRect.width / 2) - (popupRect.width / 2) + this.options.offsetX;
                top = targetRect.bottom + 10 + this.options.offsetY;
                break;
            case 'top':
            default:
                left = targetRect.left + (targetRect.width / 2) - (popupRect.width / 2) + this.options.offsetX;
                top = targetRect.top - popupRect.height - 10 + this.options.offsetY;
                break;
        }
        
        // Adjust if popup would be off-screen
        if (left < 0) left = 0;
        if (left + popupRect.width > windowWidth) left = windowWidth - popupRect.width;
        if (top < 0) top = 0;
        if (top + popupRect.height > windowHeight) top = windowHeight - popupRect.height;
        
        // Apply position
        this.element.style.left = `${left + window.scrollX}px`;
        this.element.style.top = `${top + window.scrollY}px`;
    }
    
    /**
     * Toggle popup visibility
     */
    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Clear timeouts
        clearTimeout(this.showTimeout);
        clearTimeout(this.hideTimeout);
        
        // Remove event listeners
        this.targetElement.removeEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.targetElement.removeEventListener('mouseleave', this.handleMouseLeave.bind(this));
        this.targetElement.removeEventListener('click', this.handleClick.bind(this));
        this.element.removeEventListener('mouseenter', this.handlePopupMouseEnter.bind(this));
        this.element.removeEventListener('mouseleave', this.handlePopupMouseLeave.bind(this));
        document.removeEventListener('click', this.handleDocumentClick.bind(this));
        window.removeEventListener('resize', this.handleResize.bind(this));
        window.removeEventListener('scroll', this.handleScroll.bind(this), true);
        
        // Remove from DOM
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        
        this.element = null;
    }
}